import type { ChangeEvent, FormEvent, ReactNode } from 'react';

import { Button } from '@pesto/ui/components/Button';
import { Label } from '@pesto/ui/components/Label';
import { LoadingSpinner } from '@pesto/ui/components/LoadingSpinner';
import { Typography } from '@pesto/ui/components/Typography';
import { cn } from '@pesto/utils';
import { FileUp, RotateCcw } from 'lucide-react';
import { useCallback, useId, useRef, useState } from 'react';

import type { LoanDataUploadOutcome } from './types';

import { LoanDataUploadResult } from './LoanDataUploadResult';
import { uploadLoanDataCsv } from './uploadLoanDataCsv';

const ACCEPTED_TYPES = '.csv,text/csv';

interface LoanDataUploadFormProps {
  endpoint: string;
  programId: string | null | undefined;
  getToken?: () => string | null;
  disabled?: boolean;
  disabledReason?: string;
  beforeFileField?: ReactNode;
  onUploadSuccess?: () => void;
  className?: string;
}

const isCsvFile = (file: File) => file.name.toLowerCase().endsWith('.csv') || file.type === 'text/csv';

export const LoanDataUploadForm = ({
  endpoint,
  programId,
  getToken,
  disabled,
  disabledReason,
  beforeFileField,
  onUploadSuccess,
  className,
}: LoanDataUploadFormProps) => {
  const inputId = useId();
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [file, setFile] = useState<File | null>(null);
  const [fileError, setFileError] = useState<string | null>(null);
  const [outcome, setOutcome] = useState<LoanDataUploadOutcome | null>(null);
  const [isUploading, setIsUploading] = useState(false);

  const handleFileChange = (event: ChangeEvent<HTMLInputElement>) => {
    const picked = event.target.files?.[0] ?? null;
    setFile(picked);
    setFileError(null);
  };

  const clearFile = useCallback(() => {
    setFile(null);
    setFileError(null);

    if (inputRef.current) inputRef.current.value = '';
  }, []);

  const retry = useCallback(() => {
    setOutcome(null);
    clearFile();
  }, [clearFile]);

  const submit = useCallback(
    async (event: FormEvent<HTMLFormElement>) => {
      event.preventDefault();

      if (!programId) return;

      if (!file || !isCsvFile(file)) {
        setFileError('Choose a CSV file to upload');

        return;
      }

      setIsUploading(true);
      setFileError(null);
      setOutcome(null);
      const tokenValue = getToken ? getToken() : localStorage.getItem('token');
      const result = await uploadLoanDataCsv({ endpoint, programId, file, token: tokenValue });
      setOutcome(result);
      setIsUploading(false);

      if (result.kind === 'success') onUploadSuccess?.();
    },
    [endpoint, programId, file, getToken, onUploadSuccess],
  );

  const submitDisabled = disabled || isUploading || !programId || !file || !isCsvFile(file);

  return (
    <div className={cn('flex flex-col gap-6', className)}>
      <form onSubmit={(event) => void submit(event)} className="flex flex-col gap-4" noValidate>
        {beforeFileField}
        <div className="flex flex-col gap-2">
          <Label htmlFor={inputId}>CSV file</Label>
          <div className="flex flex-wrap items-center gap-3">
            <label
              htmlFor={inputId}
              className={cn(
                'border-input hover:bg-muted/60 inline-flex h-10 cursor-pointer items-center gap-2 rounded-md border px-4 text-sm font-medium',
                disabled && 'pointer-events-none opacity-50',
              )}
            >
              <FileUp className="h-4 w-4" />
              {file ? 'Replace file' : 'Choose CSV'}
            </label>
            <input
              id={inputId}
              ref={inputRef}
              type="file"
              accept={ACCEPTED_TYPES}
              className="sr-only"
              data-testid="loan-data-upload-file-input"
              disabled={disabled}
              onChange={handleFileChange}
            />
            {file && (
              <div className="text-muted-foreground flex items-center gap-2 text-sm">
                <span className="text-foreground font-mono">{file.name}</span>
                <button
                  type="button"
                  onClick={clearFile}
                  className="text-muted-foreground hover:text-foreground text-xs underline-offset-2 hover:underline"
                >
                  Remove
                </button>
              </div>
            )}
          </div>
          {fileError && (
            <Typography variant="bodySmall" className="text-destructive">
              {fileError}
            </Typography>
          )}
          {disabled && disabledReason && (
            <Typography variant="bodySmall" className="text-muted-foreground">
              {disabledReason}
            </Typography>
          )}
        </div>
        <div className="flex items-center gap-3">
          <Button type="submit" disabled={submitDisabled} data-testid="loan-data-upload-submit">
            {isUploading && <LoadingSpinner size={16} className="mr-2" />}
            {isUploading ? 'Uploading…' : 'Upload CSV'}
          </Button>
          {outcome && (
            <Button type="button" variant="outline" onClick={retry}>
              <RotateCcw className="mr-2 h-4 w-4" />
              Upload another
            </Button>
          )}
        </div>
      </form>

      {outcome?.kind === 'success' && <LoanDataUploadResult result={outcome.data} />}
      {outcome?.kind === 'http_error' && (
        <LoanDataUploadHttpError status={outcome.status} message={outcome.body.error} onRetry={retry} />
      )}
      {outcome?.kind === 'network_error' && (
        <LoanDataUploadHttpError status={0} message={outcome.message} onRetry={retry} />
      )}
    </div>
  );
};

interface LoanDataUploadHttpErrorProps {
  status: number;
  message: string;
  onRetry: () => void;
}

const LoanDataUploadHttpError = ({ status, message, onRetry }: LoanDataUploadHttpErrorProps) => {
  const headline = ((): string => {
    if (status === 401) return 'Your session expired. Sign in again to upload.';

    if (status === 403) return 'Could not upload for this program.';

    if (status === 502) return 'S3 upload failed, please retry.';

    if (status === 400) return 'Upload was rejected.';

    if (status === 0) return 'Network error. Check your connection and try again.';

    return 'Upload failed.';
  })();

  return (
    <div
      className="border-destructive/40 bg-destructive/5 flex flex-col gap-3 rounded-md border p-4"
      data-testid="loan-data-upload-error"
    >
      <div className="flex flex-col gap-1">
        <Typography variant="headerSmall" className="text-destructive">
          {headline}
        </Typography>
        <Typography variant="bodySmall" className="text-muted-foreground">
          {message}
        </Typography>
      </div>
      <div>
        <Button variant="outline" type="button" onClick={onRetry}>
          Retry
        </Button>
      </div>
    </div>
  );
};
